import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Microscope } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, displayName);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 paper-grid opacity-[0.07]" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-white text-slate-900"><Microscope size={18} /></span>
          <span className="font-serif font-bold text-lg">truthaboutnlp.org</span>
        </Link>
        <div className="relative">
          <h2 className="font-serif font-bold text-4xl leading-tight">{t("auth.sideTitle")}</h2>
          <p className="mt-5 text-slate-300 max-w-sm leading-relaxed">{t("auth.sideDesc")}</p>
        </div>
        <div className="relative font-mono text-xs text-slate-500 uppercase tracking-widest">{t("auth.tagline")}</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="flex gap-2 mb-8 bg-slate-100 p-1 rounded-full">
            <button onClick={() => { setMode("login"); setError(""); }} data-testid="tab-login" className={`flex-1 h-9 rounded-full text-sm font-medium transition-colors ${mode === "login" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{t("auth.login")}</button>
            <button onClick={() => { setMode("register"); setError(""); }} data-testid="tab-register" className={`flex-1 h-9 rounded-full text-sm font-medium transition-colors ${mode === "register" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{t("auth.register")}</button>
          </div>

          <h1 className="font-serif font-bold text-2xl text-slate-900">{mode === "login" ? t("auth.welcomeBack") : t("auth.create")}</h1>
          <p className="mt-1.5 text-sm text-slate-500">{mode === "login" ? t("auth.loginSub") : t("auth.registerSub")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="dn" className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.displayName")}</Label>
                <Input id="dn" data-testid="register-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required className="mt-1.5 h-11" placeholder={t("auth.displayNamePh")} />
              </div>
            )}
            <div>
              <Label htmlFor="em" className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.email")}</Label>
              <Input id="em" type="email" data-testid="auth-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.password")}</Label>
              <Input id="pw" type="password" data-testid="auth-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11" placeholder="••••••••" />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

            <Button type="submit" disabled={busy} data-testid="auth-submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-full">
              {busy ? t("auth.wait") : mode === "login" ? t("auth.loginBtn") : t("auth.createBtn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            {t("auth.anonHint")} <Link to="/test/knowledge" className="text-teal-700 underline">{t("auth.anonHintLink")}</Link>.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
